/**
 * Notificacoes da equipe (spec 010): o historico do sino, com filtro por categoria e estado. Pendencia e alerta saem da
 * fila quando sao resolvidos (sozinhos, pela acao no sistema, ou aqui, com o que foi feito); aviso so informa.
 */
import { Aviso, CabecalhoDePagina, CampoSelecao, CampoTextoLongo, Carregando, Pilha, Segmentado, Selo, Tabela, Vazio, formatarDataHora, type CategoriaDaEquipe, type ContagemDaEquipe, type ItemDaEquipe, type Tom } from "@mtc/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "react-router";
import { erroDe, useApi } from "../api";
import { DialogoDeAcao } from "./DialogoDeAcao";

export type NotificacaoDaEquipe = ItemDaEquipe & { tipo: string; resolvida_em: string | null; resolvida_por: string | null; observacao: string | null };
export type SinoDaApi = { contagem: ContagemDaEquipe; alertas: NotificacaoDaEquipe[]; pendencias: NotificacaoDaEquipe[]; avisos: NotificacaoDaEquipe[] };

const CATEGORIA: Record<CategoriaDaEquipe, { rotulo: string; tom: Tom }> = {
  ALERTA: { rotulo: "Alerta", tom: "erro" },
  PENDENCIA: { rotulo: "Pendência", tom: "info" },
  AVISO: { rotulo: "Aviso", tom: "neutro" },
};
type Estado = "abertas" | "resolvidas" | "todas";

export function PaginaNotificacoes() {
  const { ler, postar } = useApi();
  const qc = useQueryClient();
  const navegar = useNavigate();
  const [categoria, setCategoria] = useState<"TODAS" | CategoriaDaEquipe>("TODAS");
  const [estado, setEstado] = useState<Estado>("abertas");
  const [resolver, setResolver] = useState<NotificacaoDaEquipe | null>(null);
  const [observacao, setObservacao] = useState("");
  const [feito, setFeito] = useState<string>();
  const busca = new URLSearchParams({ estado, limite: "100", ...(categoria !== "TODAS" ? { categoria } : {}) });
  const lista = useQuery({ queryKey: ["notificacoes", categoria, estado], queryFn: () => ler<{ itens: NotificacaoDaEquipe[] }>(`/v1/admin/notificacoes?${busca}`) });
  return (
    <Pilha espaco={5}>
      <CabecalhoDePagina titulo="Notificações" descricao="Alertas e pendências saem da fila quando são resolvidos: sozinhos, quando o trabalho é feito no sistema, ou aqui, com o que foi feito. Os avisos só informam." />
      {feito && <Aviso tipo="sucesso">{feito}</Aviso>}
      <div className="mtc-linha" style={{ gap: "var(--e-3)", flexWrap: "wrap", alignItems: "flex-end" }}>
        <Segmentado rotulo="Categoria" nome="categoria" valor={categoria} aoMudar={setCategoria} opcoes={[{ valor: "TODAS", rotulo: "Todas" }, { valor: "ALERTA", rotulo: "Alertas" }, { valor: "PENDENCIA", rotulo: "Pendências" }, { valor: "AVISO", rotulo: "Avisos" }]} />
        <CampoSelecao rotulo="Estado" valor={estado} aoMudar={(v) => setEstado(v as Estado)} opcoes={[{ valor: "abertas", rotulo: "Abertas" }, { valor: "resolvidas", rotulo: "Resolvidas" }, { valor: "todas", rotulo: "Todas" }]} />
      </div>
      {lista.isLoading ? <Carregando compacto texto="Buscando as notificações" /> : lista.isError ? <Aviso tipo="erro">{erroDe(lista.error)}</Aviso> : (
        <Tabela
          linhas={lista.data?.itens ?? []}
          chave={(n) => n.id}
          aoClicar={(n) => { if (n.link?.startsWith("/")) navegar(n.link); }}
          vazio={<Vazio titulo={estado === "abertas" ? "Nada aberto" : "Nenhuma notificação"}>{estado === "abertas" ? "Nenhum alerta ou pendência esperando. Tudo em dia." : "Nada com esse filtro."}</Vazio>}
          colunas={[
            { chave: "quando", rotulo: "Quando", celula: (n) => <span className="mtc-mono">{formatarDataHora(n.criada_em)}</span> },
            { chave: "notificacao", rotulo: "Notificação", celula: (n) => <><strong>{n.titulo}</strong><span className="mtc-tabela__apoio">{n.mensagem}</span></> },
            { chave: "categoria", rotulo: "Tipo", celula: (n) => <Selo tom={CATEGORIA[n.categoria].tom}>{CATEGORIA[n.categoria].rotulo}</Selo> },
            {
              chave: "situacao",
              rotulo: "Situação",
              celula: (n) =>
                n.categoria === "AVISO" ? <span className="mtc-texto-2">Informativo</span>
                : n.resolvida_em ? <>Resolvida por {n.resolvida_por ?? "Sistema"}<span className="mtc-tabela__apoio">{formatarDataHora(n.resolvida_em)}{n.observacao ? `. ${n.observacao}` : ""}</span></>
                : n.pode_resolver ? <button type="button" className="mtc-link" onClick={(e) => { e.stopPropagation(); setObservacao(""); setResolver(n); }}>Resolver</button>
                : <>Aberta<span className="mtc-tabela__apoio">Sai sozinha quando o pedido for decidido.</span></>,
            },
          ]}
        />
      )}
      <DialogoDeAcao
        aberto={Boolean(resolver)}
        aoFechar={() => setResolver(null)}
        titulo="Marcar como resolvida"
        descricao={resolver ? resolver.titulo : undefined}
        botao="Marcar como resolvida"
        aoEnviar={() => postar(`/v1/admin/notificacoes/${resolver!.id}/resolver`, observacao.trim() ? { observacao: observacao.trim() } : {})}
        aoConcluir={() => { setFeito(`Resolvida: ${resolver?.titulo ?? ""}.`); setResolver(null); void qc.invalidateQueries({ queryKey: ["notificacoes"] }); void qc.invalidateQueries({ queryKey: ["avisos"] }); }}
      >
        <CampoTextoLongo rotulo="O que foi feito (opcional)" valor={observacao} aoMudar={setObservacao} maxLength={500} placeholder="Ex.: devolvido por PIX em 15/09." dica="Fica na auditoria e aparece aqui pra equipe." />
      </DialogoDeAcao>
    </Pilha>
  );
}
